import React from 'react';
import { AlertCircle, ExternalLink, Lock } from 'lucide-react';
import { OrganizationsResponse } from '../../services/RepositoryService';
import { ProviderTab } from './types';

export interface OrgAccessBannersProps {
  orgsData: OrganizationsResponse | null;
  activeTab: ProviderTab;
}

const githubSettingsUrl = (clientId: string | undefined): string =>
  clientId
    ? `https://github.com/settings/connections/applications/${clientId}`
    : 'https://github.com/settings/connections/applications';

export const OrgAccessBanners: React.FC<OrgAccessBannersProps> = ({ orgsData, activeTab }) => {
  if (!orgsData) return null;

  const restricted = orgsData.organizations.filter((o) => o.accessStatus === 'restricted');
  const blocked = orgsData.organizations.filter((o) => o.accessStatus === 'blocked');

  return (
    <>
      {restricted.length > 0 && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                Some organizations haven't granted access
              </p>
              <p className="text-sm text-amber-800 dark:text-amber-300 mt-1">
                {restricted.map((o) => o.login).join(', ')}{' '}
                {restricted.length === 1 ? 'has' : 'have'} not approved Autar.{' '}
                {activeTab === 'github' ? (
                  <a
                    href={githubSettingsUrl(orgsData.githubOAuthClientId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium hover:text-amber-900 dark:hover:text-amber-100"
                  >
                    Request access in GitHub settings
                    <ExternalLink className="w-3 h-3 inline ml-1" />
                  </a>
                ) : (
                  'Ask a group owner to grant access.'
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {blocked.length > 0 && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900 dark:text-red-200">
                Organizations require admin approval
              </p>
              <p className="text-sm text-red-800 dark:text-red-300 mt-1">
                {blocked.map((o) => o.login).join(', ')}{' '}
                {blocked.length === 1
                  ? 'has third-party app restrictions enabled. An org admin must approve Autar before repositories are visible.'
                  : 'have third-party app restrictions enabled. An org admin must approve Autar before repositories are visible.'}{' '}
                {activeTab === 'github' && (
                  <a
                    href={githubSettingsUrl(orgsData.githubOAuthClientId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium hover:text-red-900 dark:hover:text-red-100"
                  >
                    Request access in GitHub settings
                    <ExternalLink className="w-3 h-3 inline ml-1" />
                  </a>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
